import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, BarChart3, TrendingUp, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InsightsWidget = ({ getAuthHeaders, navigate, isGuestMode, personalStats }) => {
  const { t } = useTranslation();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isGuestMode) {
      setInsights({
        totalEvents: 35,
        activeHolds: 11,
        activePlanners: 5,
        trendingDates: [
          { date: 'Jun 14', count: 4 },
          { date: 'Jul 4', count: 6 },
          { date: 'Aug 22', count: 3 },
        ]
      });
      setLoading(false);
      return;
    }
    const fetchInsights = async () => {
      try {
        const response = await axios.get(`${API}/insights/overview`, { headers: getAuthHeaders() });
        setInsights(response.data);
      } catch (error) {
        console.error('Failed to load insights:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInsights();
  }, [getAuthHeaders, isGuestMode]);

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6 flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!insights) return null;

  const myHolds = personalStats?.holds ?? 0;
  const myPublished = personalStats?.published ?? 0;

  return (
    <Card className="hover:shadow-lg transition-all duration-300 border-2 hover:border-teal-300 bg-[#7FD1C6] dark:bg-[#7FD1C6]/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/30 dark:bg-white/10 rounded-xl">
              <BarChart3 className="h-5 w-5 text-gray-700 dark:text-gray-200" />
            </div>
            <CardTitle className="text-lg text-gray-800 dark:text-gray-100">
              My Stats
            </CardTitle>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-xs h-7 text-gray-700 dark:text-gray-200 hover:bg-white/20"
            onClick={() => navigate('/insights')}
            data-testid="view-insights-btn"
          >
            Market View
            <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="text-center p-2 bg-white/60 dark:bg-black/20 rounded-lg">
            <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{myHolds}</p>
            <p className="text-xs text-gray-600 dark:text-gray-300">My Holds</p>
          </div>
          <div className="text-center p-2 bg-white/60 dark:bg-black/20 rounded-lg">
            <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{myPublished}</p>
            <p className="text-xs text-gray-600 dark:text-gray-300">Published</p>
          </div>
          <div className="text-center p-2 bg-white/60 dark:bg-black/20 rounded-lg">
            <p className="text-xl font-bold text-purple-600 dark:text-purple-400">{insights.plannerActivity?.activePlanners || 0}</p>
            <p className="text-xs text-gray-600 dark:text-gray-300">Planners</p>
          </div>
        </div>
        
        {insights.trendingDates?.length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Trending Dates
            </p>
            <div className="space-y-1">
              {insights.trendingDates.slice(0, 3).map((trend, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs p-1.5 bg-white/60 dark:bg-black/20 rounded">
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {new Date(trend.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">{trend.eventCount} events</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InsightsWidget;
